<template>
  <lf-dropdown
    class="priority-dropdown"
    :options="options"
    :icons="optionsIcons"
    name="task-priority-level"
    :placeholder="$t('COMMON.ALL')"
    :value="priorityValue"
    :custom-button-classnames="{ heightClassname: 'h-6-5' }"
    :toggle-from-parent
    icons-use-base
    full-width
    hide-placeholder-when-value
    @change="priorityValue = $event"
  />
</template>

<script setup lang="ts">
import { useRouteQuery } from "@vueuse/router";
import { type Component, computed } from "vue";

import startCase from "lodash/startCase";

import { TaskPriorityLevels } from "@/enums/communicationLogs";
import { priorities } from "@/helpers/constants/priorities";

import IconBullet from "@/components/icons/IconBullet.vue";

type OptionIcon = Record<
  string,
  { icon: string | Component; viewBox: string; class?: string }
>;

type Option = Record<string | TaskPriorityLevels, string>;

const { toggleFromParent = false } = defineProps<{
  toggleFromParent?: boolean;
}>();

const priorityValue = useRouteQuery("task_priority_level");

const options = computed(() =>
  Object.entries(TaskPriorityLevels).reduce<Option>(
    (prev, [key, value]) => {
      prev[value] = startCase(key);
      return prev;
    },
    { "": "All" }
  )
);

const optionsIcons = computed(() => {
  const icons = priorities.reduce<OptionIcon>(
    (prev, curr) => {
      prev[curr.value] = {
        icon: curr.icon,
        viewBox: curr.viewBox,
        class: curr.class,
      };
      return prev;
    },
    { "": { icon: IconBullet, viewBox: "-8 -8 25 25" } }
  );
  return icons;
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.priority-dropdown :deep() {
  > button {
    @apply border-grey-hover bg-grey-hover text-headline;
    & svg.rotate-180 {
      @apply text-primary;
    }
  }
  #exp_elem_list li {
    @apply flex cursor-pointer items-center justify-center px-0;
    &:hover,
    &.bg-gray-100 {
      @apply !bg-white;
    }
    span:not(.icon) {
      @apply hidden;
    }
    span.icon {
      @apply flex h-6 w-6 items-center justify-center rounded-full bg-grey-hover;
    }
  }
}
.priority-dropdown.open :deep(.absolute.bg-white) {
  @apply w-[226px];
}
.priority-dropdown.open :deep(#exp_elem_list) {
  @apply flex w-[226px] items-center space-x-1;
}
</style>
